import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { organizationsQueryOptions } from "./organization";
import { withAuth } from "@/utils/withAuth";
import { parse } from "date-fns"; // ADDED for date sorting

// Reuse the organizations query key and options since dashboard data is derived from organizations
export const dashboardQueryOptions = organizationsQueryOptions;

// Custom hook for dashboard data calculations
export const useDashboardData = () => {
  const organizationsData = useSuspenseQuery(dashboardQueryOptions);
  const organizations = organizationsData.data;

  // Calculate basic statistics
  const totalOrgs = organizations.length;
  const activeOrgs = organizations.filter(
    (org) => org.status === "LIVE",
  ).length;
  const inactiveOrgs = totalOrgs - activeOrgs;

  // Sort descending by createdAt so we get truly recent orgs
  const recentOrgs = [...organizations]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .slice(0, 5);

  // Calculate month-over-month growth
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const thisMonthOrgs = organizations.filter(
    (org) => new Date(org.createdAt) > lastMonth,
  ).length;
  const twoMonthsAgo = new Date(lastMonth);
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 1);
  const previousMonthOrgs = organizations.filter((org) => {
    const createdAt = new Date(org.createdAt);
    return createdAt > twoMonthsAgo && createdAt <= lastMonth;
  }).length;
  const growthRate = previousMonthOrgs
    ? ((thisMonthOrgs - previousMonthOrgs) / previousMonthOrgs) * 100
    : 100;

  // Calculate status distribution
  const statusDistribution = Object.entries(
    organizations.reduce(
      (acc, org) => {
        acc[org.status] = (acc[org.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    ),
  ).map(([status, count]) => ({
    name: status,
    value: count,
    percentage: (count / totalOrgs) * 100,
  }));

  // Calculate responsible person metrics
  const responsiblePersons = organizations.reduce(
    (acc, org) => {
      acc[org.responsiblePerson] = (acc[org.responsiblePerson] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const topResponsiblePersons = Object.entries(responsiblePersons)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([name, count]) => ({
      name,
      count,
      percentage: (count / totalOrgs) * 100,
    }));

  // Calculate monthly trend data
  const monthlyTrendDataUnsorted = organizations.reduce((acc: any[], org) => {
    const dt = new Date(org.createdAt);
    // e.g. "Feb 2025"
    const month = dt.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    const existing = acc.find((item) => item.month === month);
    if (existing) {
      existing.total++;
      existing[org.status] = (existing[org.status] || 0) + 1;
    } else {
      acc.push({
        month,
        total: 1,
        [org.status]: 1,
        cumulative: (acc[acc.length - 1]?.cumulative || 0) + 1,
      });
    }
    return acc;
  }, []);

  // Sort the monthly data to ensure correct chronological order, then take the last 6
  const monthlyTrendData = monthlyTrendDataUnsorted
    .sort(
      (a, b) =>
        parse(a.month, "MMM yyyy", new Date()).getTime() -
        parse(b.month, "MMM yyyy", new Date()).getTime(),
    )
    .slice(-6);

  // Calculate activity metrics
  const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const last7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const activeToday = organizations.filter(
    (org) => new Date(org.updatedAt) > last24h,
  ).length;
  const activeThisWeek = organizations.filter(
    (org) => new Date(org.updatedAt) > last7d,
  ).length;

  return {
    organizations,
    totalOrgs,
    activeOrgs,
    inactiveOrgs,
    recentOrgs,
    growthRate,
    statusDistribution,
    topResponsiblePersons,
    // Return sorted monthly trend data
    monthlyTrendData,
    activeToday,
    activeThisWeek,
    responsiblePersonCount: Object.keys(responsiblePersons).length,
  };
};

// Customer state widget

const fetchCustomerStateWidget = async () => {
  const response = await fetch("https://api.salesflare.com/opportunities", {
    headers: {
      Authorization: `Bearer ${import.meta.env.VITE_SALESFLARE_API_KEY}`,
    },
  });
  return response.json();
};

export const customerStateWidgetQueryOptions = queryOptions({
  queryKey: ["customerStateWidget"],
  queryFn: () => {
    return fetchCustomerStateWidget();
  },
  staleTime: 1000 * 60 * 2, // 2 minutes
});
